import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ConfigurationService } from '@sc/data-access-configuration';
import { InsightService } from '@sc/data-access-http-services';
import { Dossier, Fitter } from '@sc/data-access-models';
import {
  AppointmentsSandboxService,
  DossierSandboxService,
  SandboxService,
} from '@sc/data-access-state-management';
import { StorageService } from '@sc/data-access-storage';
import { TranslatorService } from '@sc/data-access-translation';
import { DialogService } from '@sc/feature-dialog';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { LoadingService } from '@sc/feature-loader';
import { ProductTourService } from '@sc/feature-product-tour';
import { SearchPageComponent } from '@sc/feature-search-page';
import { SnackbarService } from '@sc/feature-snackbar';
import { TagManagerService } from '@sc/feature-tag-manager';
import { RegisterAppService } from '@sc/register-app-actions';
import { Buffer } from 'buffer';
import { TourService } from 'ngx-ui-tour-md-menu';
import { zip } from 'rxjs';
import { mapTo, switchMap, take } from 'rxjs/operators';
import { InsurerDeclarationSteps } from '../../../../models';

@Component({
  selector: 'sc-intervention-insurer',
  styleUrls: ['./intervention.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SearchPageComponent, TranslateModule],
  template: `
    <h1 class="page-title sc-design-title-h1">
      {{ 'InsurerDeclarationIntervention.selectIntervention' | translate }}
    </h1>
    <sc-search-page
      [showListTitle]="false"
      [showPOI]="showPOI"
      (bookAppointment)="onBookAppointment($event)"
      (requestAppointment)="onRequestAppointment($event)"
      (fitterSelect)="onFitterSelect($event)"
      (navigateToExitUriEvent)="navigateToExitUri($event)"
      [withCalendar]="
        featureFlippingService.isFeatureActivated('b2b-can-display-calendar')
      "
    ></sc-search-page>
  `,
})
export class InterventionComponent implements OnInit, OnDestroy, AfterViewInit {
  showPOI = this.featureFlippingService.isFeatureActivated(
    'b2b-display-points-of-interest'
  );

  constructor(
    private readonly sandboxService: SandboxService,
    private readonly router: Router,
    private readonly loadingService: LoadingService,
    private readonly translator: TranslatorService,
    private readonly configService: ConfigurationService,
    public featureFlippingService: FeatureFlippingManagerService,
    private readonly tourService: TourService,
    private readonly productTourService: ProductTourService,
    private readonly dialog: DialogService,
    private readonly insightService: InsightService,
    private readonly snackbarService: SnackbarService,
    private readonly appointmentsSB: AppointmentsSandboxService,
    private readonly dossierSB: DossierSandboxService,
    private readonly storage: StorageService,
    private readonly tmService: TagManagerService,
    private readonly registerAppService: RegisterAppService
  ) {}
  ngAfterViewInit(): void {
    this.initProductTourSubscription();
  }

  ngOnInit(): void {
    this.insightService.trackPageView({
      name: 'Insurer search slot page',
    });
    this.snackbarService.setFocusManagement(true, {
      documentQuery: 'sc-intervention-insurer sc-search-page',
    });
    this.initNavigation();
  }

  ngOnDestroy(): void {
    this.snackbarService.setFocusManagement(false);
  }

  navigateToNextStep(
    encodedParamToPush: string,
    afterNavigate: (fulfilled: boolean) => void
  ) {
    if (
      this.featureFlippingService.isFeatureActivated(
        'b2b-redirect-after-slot-select'
      )
    ) {
      this.registerAppService.deactivateRegisterAppClose();

      window.open(
        (this.storage.getItem('redirect-uri') ||
          this.configService.getConfig().website) +
          (encodedParamToPush ? '&dossier=' + encodedParamToPush : ''),
        '_self'
      );
      return;
    }

    this.router
      .navigate(['/', 'declaration', 'declaration-end'])
      .then((fulfilled) => afterNavigate(fulfilled))
      .catch((error) => console.error(error));
  }

  onBookAppointment(bookResult: {
    fitter: Fitter;
    dossier: Dossier;
    modified: boolean;
  }) {
    this.loadingService.setLoading(true);

    let bookingSub;

    if (bookResult.modified) {
      bookingSub = this.appointmentsSB.modifyAppointment({
        dossier: bookResult.dossier,
      });
    } else {
      bookingSub = this.appointmentsSB.bookAppointment({
        dossier: bookResult.dossier,
      });
    }

    bookingSub.subscribe(
      (result) => {
        this.tmService.pushTag({
          event: 'appointment',
          dealer_name: bookResult.fitter.brand,
          dealer_location: bookResult.fitter.city,
          license_plate: bookResult.dossier.claim.car.licensePlateNumber,
          vehicle_damage: bookResult.dossier.claim.glass.glass,
        });
        this.insightService.trackEvent({
          name: `FrontApp [Insurer-Action] successfully ${
            bookResult.modified ? 'rebook dossier' : 'book new dossier from B2B'
          }`,
          properties: {
            dossier: bookResult.dossier,
          },
        });

        this.storage?.removeItem('is-dossier-access');
        this.loadingService.setLoading(false);

        let encodedParamToPush = null;
        if (
          this.featureFlippingService.isFeatureActivated(
            'integ-contract-manager-redirect-dossier-by-param'
          ) &&
          result.dossierForContractManager
        ) {
          // use this to push to the redirect_uri throught the encodedDossier param
          encodedParamToPush = Buffer.from(
            JSON.stringify(result.dossierForContractManager)
          ).toString('base64');
        }
        this.navigateToNextStep(encodedParamToPush, (fulfilled) => {
          if (fulfilled) {
            this.snackbarService.showNotification(
              this.translator.getInstantTranslation(
                'DeclarationEnd.bookAppointmentValidationTitle'
              ),
              this.translator.getInstantTranslation(
                'DeclarationEnd.bookAppointmentValidationText'
              ),
              10
            );
          }
        });
      },
      (err) => {
        this.loadingService.setLoading(false);
        // Book en echec, donc on retire l'appointment du dossier
        bookResult.dossier.appointment = null;
        this.onBookAppointmentError(err);
      }
    );
  }

  onRequestAppointment(event: { dossier: Dossier; fitter: Fitter }) {
    this.loadingService.setLoading(true);

    this.appointmentsSB
      .requestAppointment({ dossier: event.dossier })
      .pipe(
        switchMap((result) =>
          this.dossierSB.setDossier(result.dossier).pipe(mapTo(result))
        )
      )
      .subscribe(
        (result) => {
          this.tmService.pushTag({
            event: 'contact',
            dealer_name: event?.fitter?.brand,
            dealer_location: event?.fitter?.city,
            license_plate: event?.dossier?.claim?.car?.licensePlateNumber,
            vehicle_damage: event?.dossier?.claim?.glass?.glass,
          });
          this.insightService.trackEvent({
            name: `FrontApp [Insurer-Action] successfully requested appointment from B2B`,
            properties: {
              dossier: event.dossier,
            },
          });
          this.loadingService.setLoading(false);

          let encodedParamToPush = null;
          if (
            this.featureFlippingService.isFeatureActivated(
              'integ-contract-manager-redirect-dossier-by-param'
            ) &&
            result.dossierForContractManager
          ) {
            // use this to push to the redirect_uri throught the encodedDossier param
            encodedParamToPush = Buffer.from(
              JSON.stringify(result.dossierForContractManager)
            ).toString('base64');
          }
          this.navigateToNextStep(encodedParamToPush, (fulfilled) => {
            if (fulfilled) {
              this.snackbarService.showNotification(
                this.translator.getInstantTranslation(
                  'DeclarationEnd.requestAppointmentValidationTitle'
                ),
                this.translator.getInstantTranslation(
                  'DeclarationEnd.requestAppointmentValidationText'
                ),
                10
              );
            }
          });
        },
        (err) => {
          this.loadingService.setLoading(false);
          this.onBookAppointmentError(err);
        }
      );
  }

  onBookAppointmentError(error: any) {
    const errorMessageContent = error?.error?.message
      ? this.translator.getInstantTranslation(error?.error?.message)
      : this.translator.getInstantTranslation('Booking.errors.generic');

    this.dialog.showError(
      this.translator.getInstantTranslation(
        'InsurerDeclarationIntervention.reservation'
      ),
      errorMessageContent
    );
  }

  onFitterSelect(fitter: { brandId: string; fitterId: string }) {
    this.router
      .navigate([`/declaration/fitter/${fitter.brandId}/${fitter.fitterId}`])
      .catch((error) => console.error(error));
  }

  initNavigation() {
    this.sandboxService.setCurrentStep(
      InsurerDeclarationSteps.interventionStep
    );
  }

  initProductTourSubscription() {
    zip(
      this.translator.getTranslation(
        'Intervention.productTour.searchBar1.title'
      ),
      this.translator.getTranslation(
        'Intervention.productTour.searchBar1.content'
      ),
      this.translator.getTranslation(
        'Intervention.productTour.searchBar2.title'
      ),
      this.translator.getTranslation(
        'Intervention.productTour.searchBar2.content'
      ),
      this.translator.getTranslation('Intervention.productTour.filters.title'),
      this.translator.getTranslation(
        'Intervention.productTour.filters.content'
      ),
      this.translator.getTranslation('Intervention.productTour.map.title'),
      this.translator.getTranslation('Intervention.productTour.map.content')
    )
      .pipe(take(1))
      .subscribe(
        ([
          searchBarTitle,
          searchBarContent,
          filtersTitle,
          filtersContent,
          bestSlotsTitle,
          bestSlotesContent,
          firstSlotTitle,
          firstSlotContent,
        ]) => {
          this.tourService.initialize([
            {
              anchorId: 'intervention-search-bar',
              title: searchBarTitle,
              content: searchBarContent,
            },
            {
              anchorId: 'intervention-filters',
              title: filtersTitle,
              content: filtersContent,
            },
            {
              anchorId: 'intervention-best-slots',
              title: bestSlotsTitle,
              content: bestSlotesContent,
            },
            {
              anchorId: 'intervention-map',
              title: firstSlotTitle,
              content: firstSlotContent,
            },
          ]);
        }
      );

    this.productTourService.setDisplayHelp(true);

    if (this.storage.getDisplayProductTourOn(this.router.url)) {
      this.tourService.start();
    }

    this.tourService.end$.subscribe(() => {
      this.storage.setDisplayProductTourOn(this.router.url, false);
    });
  }

  navigateToExitUri(dossierForContractManager?: any) {
    this.registerAppService.navigateToExitUri(dossierForContractManager);
  }
}
